import React from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import { AssignmentAssociate } from '../../assignments/models';
import { formatDisplayName } from '../../utils';
import AssociateAvatar from '../../components/AssociateAvatar';

export interface AssociateRowProps {
  associate: AssignmentAssociate;
  stationLabel: string;
}

/**
 * A single row on the Labor board (Associate View Page).
 *
 * Pure presentational: three horizontal slots.
 *   - Left:   circular avatar showing the associate's badge photo (from
 *             `associate.associateAlias`), falling back to 1–2 character
 *             initials derived from `associate.associateName`.
 *   - Middle: the compact display name, `formatDisplayName(associateName)`.
 *   - Right:  the caller-supplied `stationLabel` string, right-aligned via
 *             the page-level SCSS (see `index.scss`).
 *
 * The component is strictly read-only: no click handlers, no buttons, no
 * inputs, no mutation controls of any kind (Req 7.1, 7.2). Layout is done
 * with Meridian `Box` and text is rendered via Meridian `Text`; the actual
 * grid / avatar styling lives in the page-level SCSS created in task 9.2,
 * which keys off the `associate-view-row` class names below.
 *
 * The root element carries `data-testid="associate-view-row"` plus
 * `data-associate-id={associate.associateId}` so tests can target a
 * specific row without relying on DOM order.
 */
const AssociateRow: React.FC<AssociateRowProps> = ({ associate, stationLabel }) => {
  const displayName = formatDisplayName(associate.associateName);

  return (
    <Box
      className="associate-view-row"
      data-testid="associate-view-row"
      data-associate-id={associate.associateId}
    >
      <AssociateAvatar
        associateName={associate.associateName}
        associateAlias={associate.associateAlias}
        className="associate-view-row__avatar"
      />
      <Box className="associate-view-row__name">
        <Text type="b300">{displayName}</Text>
      </Box>
      <Box className="associate-view-row__station">
        <Text type="b300">{stationLabel}</Text>
      </Box>
    </Box>
  );
};

export default AssociateRow;
